import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'motion/react';
// @mui
import { styled, Theme, useTheme } from '@mui/material/styles';
import { Box, Stack, Drawer } from '@mui/material';
// hooks
import useResponsive from '../../../hooks/useResponsive';
import useCollapseDrawer from '../../../hooks/useCollapseDrawer';
// config
import { NAVBAR } from '../../../config';
// components
import ReachLinkLogo from '../../../components/Logo';
import CoBrandingNavLogo from '../../../components/co-branding/CoBrandingNavLogo';
import useCoBranding from '../../../components/co-branding/useCoBranding';
import Scrollbar from '../../../components/Scrollbar';
import { NavSectionVertical } from '../../../components/nav-section';
//
import NavbarAccount from './NavbarAccount';
import ClientNavbarAccount from './ClientNavbarAccount';
import CollapseButton from './CollapseButton';
import useNavConfig from '../../../hooks/useNavConfig';
import NavbarHelp from './NavbarHelp';
import useAuth from '../../../hooks/useAuth';
import CareCircleBanner from 'src/components/carecircle/CareCircleBanner';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
    [theme.breakpoints.up('lg')]: {
        flexShrink: 0,
        transition: theme.transitions.create('width', {
            duration: theme.transitions.duration.shorter,
        }),
    },
}));

// ----------------------------------------------------------------------

type Props = {
    isOpenSidebar: boolean;
    onCloseSidebar: VoidFunction;
};

export default function NavbarVertical({ isOpenSidebar, onCloseSidebar }: Props) {
    const theme = useTheme();

    const { isClient, user } = useAuth();
    const { coBranding } = useCoBranding();
    const isReachlinkPlusOffered = user?.reachlinkPlusEnabled !== false;

    const { pathname } = useLocation();
    const { navConfig } = useNavConfig();

    const isDesktop = useResponsive('up', 'lg');

    const { isCollapse, collapseClick, collapseHover, onToggleCollapse } = useCollapseDrawer();

    useEffect(() => {
        if (isOpenSidebar) {
            onCloseSidebar();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    const renderContent = (
        <Stack
            sx={{
                height: 1,
                backgroundColor: isClient
                    ? theme.palette.background.default
                    : theme.palette.background.paper,
            }}
        >
            <Scrollbar
                sx={{
                    '& .simplebar-content': { display: 'flex', flexDirection: 'column' },
                }}
            >
                <Stack
                    spacing={3}
                    sx={{
                        pt: 3,
                        pb: 2,
                        px: 2.5,
                        flexShrink: 0,
                        ...(isCollapse && { pb: 1, alignItems: 'center' }),
                    }}
                >
                    <Stack
                        direction={isCollapse ? 'column' : 'row'}
                        alignItems={isCollapse ? 'center' : 'end'}
                        justifyContent="space-between"
                        spacing={isCollapse ? 2 : 0}
                    >
                        {!isCollapse && coBranding ? (
                            <CoBrandingNavLogo />
                        ) : (
                            <ReachLinkLogo mini={isCollapse} width={isCollapse ? '65%' : '80%'} />
                        )}
                        {isCollapse && !isClient && <NavbarAccount isCollapse={isCollapse} />}
                        {isDesktop && (
                            <CollapseButton
                                onToggleCollapse={onToggleCollapse}
                                collapseClick={collapseClick}
                            />
                        )}
                    </Stack>
                </Stack>
                {!isCollapse && !isClient && (
                    <Stack sx={{ px: 2 }}>
                        <NavbarAccount isCollapse={isCollapse} />
                    </Stack>
                )}

                <NavSectionVertical navConfig={navConfig} isCollapse={isCollapse} />
            </Scrollbar>

            {!isCollapse && isReachlinkPlusOffered && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, ease: 'easeOut', delay: 0.25 }}
                >
                    <CareCircleBanner />
                </motion.div>
            )}
            <NavbarHelp isCollapse={isCollapse} />
            {isClient && <ClientNavbarAccount isCollapse={isCollapse} />}
        </Stack>
    );

    return (
        <RootStyle
            sx={{
                width: {
                    lg: isCollapse ? NAVBAR.MAIN_COLLAPSE_WIDTH : NAVBAR.MAIN_WIDTH,
                },
                ...(collapseClick && {
                    position: 'absolute',
                }),
            }}
        >
            {!isDesktop && (
                <Drawer
                    open={isOpenSidebar}
                    onClose={onCloseSidebar}
                    PaperProps={{
                        sx: {
                            width: NAVBAR.MAIN_WIDTH,
                            backgroundColor: theme.palette.background.default,
                        },
                    }}
                >
                    {renderContent}
                </Drawer>
            )}

            {isDesktop && (
                <Drawer
                    open
                    variant="persistent"
                    PaperProps={{
                        sx: {
                            width: NAVBAR.MAIN_WIDTH,
                            borderRightStyle: isClient ? 'dashed' : 'solid',
                            backgroundColor: theme.palette.background.default,
                            transition: (theme: Theme) =>
                                theme.transitions.create('width', {
                                    duration: theme.transitions.duration.standard,
                                }),
                            ...(isCollapse && {
                                width: NAVBAR.MAIN_COLLAPSE_WIDTH,
                            }),
                            ...(collapseHover && {
                                boxShadow: (theme: Theme) => theme.customShadows.z24,
                            }),
                        },
                    }}
                >
                    {renderContent}
                </Drawer>
            )}
        </RootStyle>
    );
}
